import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { logError, logWarn } from '@/lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'tulsijobs_token';

axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(undefined); // undefined = loading, null = no user, {} = logged in
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  // Wire axios Authorization header whenever token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common.Authorization;
    }
  }, [token]);

  // On mount + when token changes: try to load /me
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!token) {
        if (!cancelled) setUser(null);
        return;
      }
      try {
        const { data } = await axios.get(`${API}/auth/me`);
        if (!cancelled) setUser(data);
      } catch (e) {
        if (!cancelled) {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
          setUser(null);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [token]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/auth/logout`); }
    catch (err) { logWarn('Logout request failed (clearing client session anyway):', err?.message); }
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const isOwner = user?.role === 'owner';
  const permissions = useMemo(() => new Set(user?.permissions || []), [user]);
  const hasPerm = useCallback((m) => isOwner || permissions.has(m), [isOwner, permissions]);
  const canManageCompanies = hasPerm('companies');
  const canSeeFinance = hasPerm('finance');

  const value = useMemo(
    () => ({ user, token, login, logout, isOwner, canManageCompanies, canSeeFinance, hasPerm, permissions }),
    [user, token, login, logout, isOwner, canManageCompanies, canSeeFinance, hasPerm, permissions]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
